import * as React from 'react';

import * as AvatarPrimitive from '@radix-ui/react-avatar';

import { cn } from '@/lib/utils';

const sizeMap = {
  xxs: 'size-4',
  xs: 'size-6',
  sm: 'size-8',
  md: 'size-10',
  lg: 'size-12',
  xl: 'size-14',
  '2xl': 'size-16',
} as const;

const roundedMap = {
  xxs: 'rounded',
  xs: 'rounded-md',
  sm: 'rounded-lg',
  md: 'rounded-lg',
  lg: 'rounded-xl',
  xl: 'rounded-xl',
  '2xl': 'rounded-2xl',
} as const;

const fallbackFontMap = {
  xxs: 'text-[8px] font-medium leading-[8px]',
  xs: 'text-xs font-normal leading-3',
  sm: 'text-sm font-normal leading-none',
  md: 'text-base font-normal leading-none',
  lg: 'text-lg font-normal leading-none',
  xl: 'text-xl font-normal leading-tight',
  '2xl': 'text-2xl font-normal leading-normal',
} as const;

type AvatarSize = keyof typeof sizeMap;

interface AvatarProps extends React.ComponentProps<typeof AvatarPrimitive.Root> {
  size?: AvatarSize;
  isRounded?: boolean;
}

const Avatar = ({
  className,
  size = 'md',
  isRounded = false,
  ref,
  ...props
}: AvatarProps & { ref?: React.Ref<React.ElementRef<typeof AvatarPrimitive.Root>> }) => {
  return (
    <AvatarPrimitive.Root
      ref={ref}
      data-slot="avatar"
      className={cn(
        'relative flex shrink-0 overflow-hidden',
        sizeMap[size],
        isRounded ? roundedMap[size] : 'rounded-full',
        className
      )}
      {...props}
    />
  );
};

const AvatarImage = ({
  className,
  ref,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Image> & {
  ref?: React.Ref<React.ElementRef<typeof AvatarPrimitive.Image>>;
}) => {
  return (
    <AvatarPrimitive.Image
      ref={ref}
      data-slot="avatar-image"
      className={cn('aspect-square size-full', className)}
      {...props}
    />
  );
};

const AvatarFallback = ({
  className,
  size = 'md',
  ref,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Fallback> & { size?: AvatarSize } & {
  ref?: React.Ref<React.ElementRef<typeof AvatarPrimitive.Fallback>>;
}) => {
  return (
    <AvatarPrimitive.Fallback
      ref={ref}
      data-slot="avatar-fallback"
      className={cn(
        'flex size-full items-center justify-center rounded-full bg-muted text-muted-foreground',
        fallbackFontMap[size],
        className
      )}
      {...props}
    />
  );
};

export { Avatar, AvatarImage, AvatarFallback };
export type { AvatarProps, AvatarSize };
